import { Box, Chip, Tooltip } from "@mui/joy";
import type { MailPlaceholderDefinition } from "api/mailTemplates/MailTemplate.typed";
import { colorTokens } from "../../../../styles/globalStyles";
import { mailPlaceholderPresentableLabel, mailPlaceholderTooltip } from "../mailEditorShared";
import { setMailPlaceholderDragData } from "../mailEditorDnD";

type MailEditorPlaceholderChipsProps = {
  placeholdersForInsert: MailPlaceholderDefinition[];
  canEdit: boolean;
  onInsertPlaceholder: (key: string) => void;
  labelLookupCatalog?: MailPlaceholderDefinition[];
  interactiveKeys?: ReadonlySet<string>;
  allowDrag?: boolean;
};

export default function MailEditorPlaceholderChips(props: MailEditorPlaceholderChipsProps) {
  const {
    placeholdersForInsert,
    canEdit,
    onInsertPlaceholder,
    labelLookupCatalog,
    interactiveKeys,
    allowDrag = true,
  } = props;
  const catalogForLabel = labelLookupCatalog ?? placeholdersForInsert;

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {placeholdersForInsert.map((d) => {
        const interactive = canEdit && (interactiveKeys == null || interactiveKeys.has(d.key));
        const dragEnabled = interactive && allowDrag;

        return (
          <Tooltip key={d.key} title={mailPlaceholderTooltip(d)}>
            <Box component="span" sx={{ display: "inline-flex", maxWidth: "100%" }}>
              <Chip
                size="sm"
                variant="outlined"
                draggable={dragEnabled}
                disabled={!interactive}
                onMouseDown={(e) => {
                  if (e.button !== 0 || !interactive) {
                    return;
                  }

                  e.preventDefault();

                  if (!allowDrag) {
                    onInsertPlaceholder(d.key);
                  }
                }}
                onDragStart={(e) => {
                  if (dragEnabled) {
                    setMailPlaceholderDragData(e, d.key);
                  } else {
                    e.preventDefault();
                  }
                }}
                onClick={() => {
                  if (interactive && allowDrag) {
                    onInsertPlaceholder(d.key);
                  }
                }}
                sx={{
                  borderColor: colorTokens.border.strong,
                  maxWidth: "100%",
                  fontSize: 12,
                  cursor: !interactive ? "default" : dragEnabled ? "grab" : "pointer",
                  touchAction: dragEnabled ? "none" : "auto",
                  "&:active": {
                    cursor: !interactive ? "default" : dragEnabled ? "grabbing" : "pointer",
                  },
                }}
              >
                {mailPlaceholderPresentableLabel(d.key, catalogForLabel)}
              </Chip>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
